import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import axios from 'axios';

const KubernetesWorkloadsPage = () => {
    const { selectedAKSCluster } = useSelector((state: RootState) => state.monitoring);

    const [workloads, setWorkloads] = useState<any>({
        deployments: [],
        statefulSets: [],
        daemonSets: []
    });
    const [loading, setLoading] = useState<boolean>(true);
    const [filter, setFilter] = useState<string>('');
    const [activeTab, setActiveTab] = useState<string>('deployments');

    useEffect(() => {
        const fetchWorkloads = async () => {
            try {
                setLoading(true);
                const response = await axios.get('/api/monitoring/workloads');
                setWorkloads(response.data.data || response.data);
                setLoading(false);
            } catch (error) {
                console.error('Error fetching workloads:', error);
                setLoading(false);
            }
        };

        fetchWorkloads();
    }, []);

    // Filter workloads by selected AKS cluster from sidebar
    const getFilteredWorkloads = (workloadList: any[]) => {
        let filtered = selectedAKSCluster
            ? workloadList.filter((workload: any) => workload.cluster === selectedAKSCluster.name)
            : workloadList;

        // Apply text filter
        if (filter) {
            filtered = filtered.filter((workload: any) =>
                workload.name.toLowerCase().includes(filter.toLowerCase()) ||
                workload.namespace.toLowerCase().includes(filter.toLowerCase())
            );
        }

        return filtered;
    };

    const filteredDeployments = getFilteredWorkloads(workloads.deployments);
    const filteredStatefulSets = getFilteredWorkloads(workloads.statefulSets);
    const filteredDaemonSets = getFilteredWorkloads(workloads.daemonSets);

    const getTotalCount = () => {
        return workloads.deployments.length + workloads.statefulSets.length + workloads.daemonSets.length;
    };

    const getFilteredCount = () => {
        return filteredDeployments.length + filteredStatefulSets.length + filteredDaemonSets.length;
    };

    if (loading) {
        return (
            <div className="container mx-auto px-4 py-6">
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                    <span className="ml-3 text-gray-600 dark:text-gray-400">Loading workloads...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-6">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Kubernetes Workloads</h1>
                    {selectedAKSCluster && (
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                            Filtered by: {selectedAKSCluster.name}
                        </p>
                    )}
                </div>
                {selectedAKSCluster && (
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                        Showing {getFilteredCount()} of {getTotalCount()} total workloads
                    </div>
                )}
            </div>

            <div className="mb-6">
                <input
                    type="text"
                    placeholder="Filter by name or namespace..."
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md w-full bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    value={filter}
                    onChange={e => setFilter(e.target.value)}
                />
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
                <nav className="-mb-px flex space-x-8">
                    <button
                        className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'deployments'
                                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
                            }`}
                        onClick={() => setActiveTab('deployments')}
                    >
                        Deployments ({filteredDeployments.length})
                    </button>
                    <button
                        className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'statefulsets'
                                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
                            }`}
                        onClick={() => setActiveTab('statefulsets')}
                    >
                        StatefulSets ({filteredStatefulSets.length})
                    </button>
                    <button
                        className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'daemonsets'
                                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
                            }`}
                        onClick={() => setActiveTab('daemonsets')}
                    >
                        DaemonSets ({filteredDaemonSets.length})
                    </button>
                </nav>
            </div>

            {/* Deployments Tab */}
            {activeTab === 'deployments' && (
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Name</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Namespace</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Replicas</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Available</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Created</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {filteredDeployments.map((deployment: any, index: number) => (
                                <tr key={`${deployment.namespace}-${deployment.name}-${index}`} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                    <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900 dark:text-white">
                                        {deployment.name}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-gray-500 dark:text-gray-400">
                                        {deployment.namespace}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-gray-900 dark:text-white">
                                        {deployment.replicas}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${deployment.availableReplicas === deployment.replicas
                                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                                : deployment.availableReplicas > 0
                                                    ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                                    : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                            }`}>
                                            {deployment.availableReplicas}/{deployment.replicas}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                        {new Date(deployment.creationTimestamp).toLocaleDateString()}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {filteredDeployments.length === 0 && (
                        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                            {filter ? 'No deployments match your filter' :
                                selectedAKSCluster ? `No deployments found in ${selectedAKSCluster.name}` :
                                    'No deployments found'}
                        </div>
                    )}
                </div>
            )}

            {/* StatefulSets Tab */}
            {activeTab === 'statefulsets' && (
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Name</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Namespace</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Replicas</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Ready</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Created</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {filteredStatefulSets.map((statefulSet: any, index: number) => (
                                <tr key={`${statefulSet.namespace}-${statefulSet.name}-${index}`} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                    <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900 dark:text-white">
                                        {statefulSet.name}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-gray-500 dark:text-gray-400">
                                        {statefulSet.namespace}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-gray-900 dark:text-white">
                                        {statefulSet.replicas}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${statefulSet.readyReplicas === statefulSet.replicas
                                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                                : statefulSet.readyReplicas > 0
                                                    ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                                    : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                            }`}>
                                            {statefulSet.readyReplicas}/{statefulSet.replicas}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                        {new Date(statefulSet.creationTimestamp).toLocaleDateString()}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {filteredStatefulSets.length === 0 && (
                        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                            {filter ? 'No stateful sets match your filter' :
                                selectedAKSCluster ? `No stateful sets found in ${selectedAKSCluster.name}` :
                                    'No stateful sets found'}
                        </div>
                    )}
                </div>
            )}

            {/* DaemonSets Tab */}
            {activeTab === 'daemonsets' && (
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Name</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Namespace</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Desired</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Ready</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Created</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {filteredDaemonSets.map((daemonSet: any, index: number) => (
                                <tr key={`${daemonSet.namespace}-${daemonSet.name}-${index}`} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                    <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900 dark:text-white">
                                        {daemonSet.name}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-gray-500 dark:text-gray-400">
                                        {daemonSet.namespace}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-gray-900 dark:text-white">
                                        {daemonSet.desiredNumberScheduled}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${daemonSet.numberReady === daemonSet.desiredNumberScheduled
                                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                                : daemonSet.numberReady > 0
                                                    ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                                    : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                            }`}>
                                            {daemonSet.numberReady}/{daemonSet.desiredNumberScheduled}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                        {new Date(daemonSet.creationTimestamp).toLocaleDateString()}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {filteredDaemonSets.length === 0 && (
                        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                            {filter ? 'No daemon sets match your filter' :
                                selectedAKSCluster ? `No daemon sets found in ${selectedAKSCluster.name}` :
                                    'No daemon sets found'}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default KubernetesWorkloadsPage;